import type { EventInstanceRecord } from '../api/types';

export function eventTime(event: EventInstanceRecord): number {
  if (!event.startsAt) return Number.MAX_SAFE_INTEGER;
  const time = Date.parse(event.startsAt);
  return Number.isFinite(time) ? time : Number.MAX_SAFE_INTEGER;
}

export function eventDateParts(event: EventInstanceRecord, locale: 'cs' | 'en') {
  if (eventTime(event) === Number.MAX_SAFE_INTEGER) return undefined;
  const zone = safeZone(event.timezone);
  const date = new Date(event.startsAt!);
  return {
    day: new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: zone.value }).formatToParts(date).find(part => part.type === 'day')!.value,
    month: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: zone.value }).formatToParts(date).find(part => part.type === 'month')!.value,
    year: new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: zone.value }).format(date),
    utcFallback: zone.fallback,
  };
}

export function fullDate(event: EventInstanceRecord, locale: 'cs' | 'en'): string | undefined {
  if (eventTime(event) === Number.MAX_SAFE_INTEGER) return undefined;
  const zone = safeZone(event.timezone);
  return new Intl.DateTimeFormat(locale === 'cs' ? 'cs-CZ' : 'en-GB', { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: zone.value }).format(new Date(event.startsAt!)) + (zone.fallback ? ' (UTC)' : '');
}

export function shortDate(event: EventInstanceRecord, locale: 'cs' | 'en') {
  if (eventTime(event) === Number.MAX_SAFE_INTEGER) return undefined;
  const date = new Date(event.startsAt!);
  const zone = safeZone(event.timezone);
  const monthParts = new Intl.DateTimeFormat(locale === 'cs' ? 'cs-CZ' : 'en-GB', {
    month: locale === 'cs' ? 'numeric' : 'short', year: 'numeric', timeZone: zone.value,
  }).formatToParts(date);
  const month = monthParts.find(part => part.type === 'month')!.value;
  const year = monthParts.find(part => part.type === 'year')!.value;
  return {
    day: new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: zone.value }).formatToParts(date).find(part => part.type === 'day')!.value,
    month: locale === 'cs' ? `${month}. ${year}` : `${month} ${year}`,
    weekday: new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: zone.value }).format(date),
    utcFallback: zone.fallback,
  };
}

function safeZone(value: string): { value: string; fallback: boolean } {
  try { new Intl.DateTimeFormat('en', { timeZone: value }); return { value, fallback: false }; }
  catch { return { value: 'UTC', fallback: true }; }
}
